import React from 'react';
import '../../LobbyPage/desktop/LobbyDesktop.css';
import '../../LobbyPage/desktop/LobbyControlBar.css';
import './HotSeatDesktop.css';
import PageButton from '../../elements/PageButton/PageButton';
import {getOpponentsGridLayout} from '../../LobbyPage/desktop/getOpponentsGridLayout';
import HotSeatFieldCard from '../HotSeatFieldCard';
import {
  HotSeatPlayerBonusToggle,
  HotSeatPlayerBotToggle,
  HotSeatPlayerNameField,
} from '../HotSeatPlayerControls';
import {getFactionConfig} from '../../../utils/factions/getFactionConfig';
import {getFactionSkinArtPath} from '../../../utils/factions/getFactionSkinArtPath';
import {getPlayerHexColor} from '../../../utils/players/getPlayerHexColor';
import {pluralizeRu} from '../../../utils/other/pluralizeRu';
import {RANDOM_FACTION_CONFIG} from '../../../configs/factions';
import {
  GROUND_BACKGROUND_CHOICE_TEXT,
  getGroundBackgroundChoiceName,
} from '../../../configs/groundBackgroundChoice';

const PAGE_ID = 'HotSeatPlayersInitPage';

/**
 * Подпись под кнопкой «Настройки»: что сейчас выбрано на экране настроек.
 * В сетевом лобби те же настройки лежат в полосе — здесь они на своем экране,
 * но выбранное должно быть видно до старта.
 *
 * @param {Object} settings
 * @param {number} settings.roundCount
 * @param {boolean} settings.isTimerEnabled
 * @param {boolean} settings.isSpectatorMode
 * @param {string|null} settings.groundBackground
 * @param {boolean} settings.isMissionEditorMode — в редакторе миссий выбора биома нет
 * @returns {string}
 */
export const getHotSeatSettingsHint = ({
  roundCount, isTimerEnabled, isSpectatorMode, groundBackground, isMissionEditorMode,
}) => [
  `${roundCount} ${pluralizeRu(roundCount, 'раунд', 'раунда', 'раундов')}`,
  `таймер ${isTimerEnabled ? 'вкл' : 'выкл'}`,
  isSpectatorMode ? 'режим зрителя' : null,
  isMissionEditorMode
    ? null
    : `${GROUND_BACKGROUND_CHOICE_TEXT.label}: ${getGroundBackgroundChoiceName(groundBackground)}`,
].filter(Boolean).join(' · ');

/**
 * Карточка игрока: большой арт фракции (нажатие — выбор фракции), под ним имя,
 * бот или живой игрок и бонус. Полоса сверху — цвет игрока: тем же цветом его
 * флаги на схеме поля. Фракция здесь не тайна, как у соперников в лобби: все
 * игроки партии настраиваются с одного экрана.
 */
const HotSeatPlayerCard = ({
  player, isYou, skinId, hasError, isMissionEditorMode, onNameChange, onBotToggle, onBonusCycle, onOpenFactionSelect,
}) => {
  const {color, name, isBot, factionId, bonus} = player;
  const factionConfig = factionId ? getFactionConfig(factionId) : null;
  const isLocalBot = isBot && !isMissionEditorMode;

  return (
    <article
      className={`hot-seat-player-card ${isYou ? 'hot-seat-player-card_own' : ''}`}
      style={{'--hot-seat-player-color': getPlayerHexColor(color)}}
      data-player-color={color}
    >
      <button
        type="button"
        className="hot-seat-player-card_art"
        onClick={() => onOpenFactionSelect(color)}
        title={factionConfig ? `Фракция: ${factionConfig.name}. Нажмите, чтобы изменить.` : 'Фракция будет выбрана случайно. Нажмите, чтобы выбрать самостоятельно.'}
      >
        {factionConfig ? (
          // key — чтобы браузер не держал прежний арт, пока грузится новый
          <img
            key={factionId}
            className="hot-seat-player-card_art-image"
            src={getFactionSkinArtPath(factionId, skinId)}
            alt=""
          />
        ) : (
          <img
            className="hot-seat-player-card_art-image hot-seat-player-card_art-image_random"
            src={RANDOM_FACTION_CONFIG.imagePath}
            alt=""
          />
        )}
        <span className="hot-seat-player-card_faction">
          {factionConfig ? factionConfig.name : 'Случайная фракция'}
        </span>
      </button>

      <div className="hot-seat-player-card_controls">
        <HotSeatPlayerNameField color={color} name={name} hasError={hasError} isYou={isYou} onChange={onNameChange}/>
        <div className="hot-seat-player-card_toggles">
          <HotSeatPlayerBotToggle
            color={color} isBot={isBot} isLocalBot={isLocalBot} bonus={bonus} onToggle={onBotToggle}
          />
          <HotSeatPlayerBonusToggle color={color} bonus={bonus} isLocalBot={isLocalBot} onCycle={onBonusCycle}/>
        </div>
      </div>
    </article>
  );
};

/**
 * Десктопная раскладка настройки локальной партии (ширина от 1024 px) — та же,
 * что у десктопного лобби (LobbyDesktop): шапка, ниже схема поля слева и сетка
 * карточек игроков справа, внизу полоса всех кнопок. Помещается в экран без
 * прокрутки. Логика и состояние — в HotSeatPlayersInitPage; ветка телефона там же.
 */
const HotSeatDesktop = ({
  field, fieldPreviewModel, players, youPlayerColor, factionLoadouts, errors, isMissionEditorMode, settingsHint,
  onNameChange, onBotToggle, onBonusCycle, onOpenFactionSelect, onSubmit, onOpenSettings, onBack,
}) => (
  <div className="lobby-desktop hot-seat-desktop">
    <header className="lobby-desktop_header">
      <div className="lobby-desktop_heading">
        <h1 className="lobby-desktop_title">Настройка игроков</h1>
        <div className="lobby-desktop_subtitle">
          {isMissionEditorMode ? 'Редактор миссии' : 'Игра против ботов'} · Игроков: {players.length}
        </div>
      </div>
    </header>

    <div className="lobby-desktop_main">
      <div className="lobby-desktop_own">
        {fieldPreviewModel && <HotSeatFieldCard field={field} model={fieldPreviewModel}/>}
      </div>

      <div className="lobby-desktop_opponents" data-layout={getOpponentsGridLayout(players.length)}>
        {players.map((player) => (
          <HotSeatPlayerCard
            key={player.color}
            player={player}
            isYou={player.color === youPlayerColor}
            skinId={player.factionId ? factionLoadouts?.[player.factionId]?.selectedSkin : undefined}
            hasError={Boolean(errors[player.color])}
            isMissionEditorMode={isMissionEditorMode}
            onNameChange={onNameChange}
            onBotToggle={onBotToggle}
            onBonusCycle={onBonusCycle}
            onOpenFactionSelect={onOpenFactionSelect}
          />
        ))}
      </div>
    </div>

    <footer className="lobby-control-bar">
      <div className="lobby-control-bar_group lobby-control-bar_left">
        <PageButton className="lobby-bar-button" onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>

      <div className="lobby-control-bar_group lobby-control-bar_center">
        <PageButton
          className="lobby-bar-button lobby-bar-button_with-hint"
          onClick={onOpenSettings}
          pageId={PAGE_ID}
          buttonId="settings"
        >
          <span className="lobby-bar-button_label">Настройки</span>
          <span className="lobby-bar-button_hint hot-seat-desktop_settings-hint">{settingsHint}</span>
        </PageButton>
      </div>

      <div className="lobby-control-bar_group lobby-control-bar_right">
        {/* Как «Начать матч» в лобби, когда все готовы: красная и пульсирует —
            здесь игроки готовы всегда */}
        <PageButton
          className="lobby-bar-button lobby-bar-button_primary hot-seat-desktop_start lobby-button-pulse"
          onClick={onSubmit}
          pageId={PAGE_ID}
          buttonId="start_game"
        >
          Начать матч
        </PageButton>
      </div>
    </footer>
  </div>
);

export default HotSeatDesktop;
